import { useEffect, useState } from 'react'
import { api } from '../../../services/api'

export const PATHWAY_AXES = [
  'far_or_nonfar',
  'aaf_strategy',
  'instrument',
  'competition',
  'set_aside_class',
  'pricing',
  'notice_class',
] as const

export type PathwayAxis = (typeof PATHWAY_AXES)[number]

export interface PathwayRow {
  far_or_nonfar: string
  aaf_strategy: string
  instrument: string
  competition: string
  set_aside_class: string
  pricing: string
  notice_class: string
  award_count: number
  total_obligated: number
  awards_with_days_open: number
  awards_with_days_eval: number
  awards_with_log_amount: number
  days_open_bins: number[]
  days_eval_bins: number[]
  log_amount_bins: number[]
}

export interface PathwayFlowFilters {
  fiscalYear?: number | number[]
  portfolioGroup?: string
  officeCode?: string
  subAgencyCode?: string
  macomCode?: string
}

export function usePathwayFlowQuery(filters: PathwayFlowFilters) {
  const [data, setData] = useState<PathwayRow[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)

  useEffect(() => {
    setLoading(true)
    const params: Record<string, string> = {}
    if (filters.fiscalYear !== undefined) {
      params.fiscal_year = Array.isArray(filters.fiscalYear)
        ? filters.fiscalYear.join(',')
        : String(filters.fiscalYear)
    }
    if (filters.portfolioGroup) params.portfolio_group = filters.portfolioGroup
    if (filters.officeCode) params.office_code = filters.officeCode
    if (filters.subAgencyCode) params.sub_agency_code = filters.subAgencyCode
    if (filters.macomCode) params.macom_code = filters.macomCode

    api
      .getRaw<PathwayRow[]>('/api/govdata/pathway-flow/', params)
      .then((rows) => {
        setData(Array.isArray(rows) ? rows : [])
        setError(null)
      })
      .catch((e) => setError(e?.message || 'Failed to load pathway flow'))
      .finally(() => setLoading(false))
  }, [
    filters.fiscalYear,
    filters.portfolioGroup,
    filters.officeCode,
    filters.subAgencyCode,
    filters.macomCode,
  ])

  return { data, loading, error }
}

// Bin schemas — must match Velocity-Defense/data:govdata/dbt/models/marts/pathway_flow.sql
export const DAYS_OPEN_BIN_WIDTH = 7
export const DAYS_EVAL_BIN_WIDTH = 30
export const LOG_AMOUNT_BIN_WIDTH = 0.6
export const N_BINS = 30
